import {
  CollapsibleDiv,
  Dropdown,
  DropdownMenu,
  DropdownRef,
  Input,
  Pill,
  Select,
  Typography,
} from '@recruit-robin/robin-theme/web-components';
import { ChevronDown, ChevronUp } from '@recruit-robin/robin-theme/web-icons';
import { useContext, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import {
  DRIVER_LICENSE_CATEGORIES,
  EDUCATION_LEVELS,
  LANGUAGE_LEVELS,
  languageOptions,
} from '../../../../utils/EnrichVacancyModal.constants';
import { JobFormValues } from '../../EditEnrichedVacancy.types';
import { CustomSelectForm } from './CustomSelectForm';
import { useStyles } from './FormSections.styles';
import { RenderField } from './RenderField';
import { useQualityStatus } from './useQualityStatus';

export const Requirements = () => {
  const classes = useStyles();
  const { dictionary, language } = useContext(LocalizationContext);
  const languagesLevelDropdownRef = useRef<DropdownRef>(null);
  const { control } = useFormContext<JobFormValues>();

  const formattedLanguageOptions = useMemo(
    () => languageOptions.map((opt) => ({ value: opt.value, label: opt.label })),
    [language],
  );

  const fieldConfig = {
    minimum_education_level: { required: true, weight: 20 },
    maximum_language_skills: { weight: 20 },
    languages_level: { weight: 20 },
    certificates: { weight: 20 },
    drivers_license: { weight: 20 },
  };

  const { statusConfig } = useQualityStatus<JobFormValues>('requirements', fieldConfig);

  return (
    <CollapsibleDiv
      initialOpen
      limitToggleToTitle
      key="requirements"
      title={
        <Typography variant={(v) => v.supporting.label} color={(c) => c.neutral.variant[70]}>
          {dictionary.jobRequirements}
        </Typography>
      }
      endAdornment={
        <Pill
          startIcon={statusConfig.icon}
          label={statusConfig.label}
          style="icon-removable"
          type={statusConfig.type}
          padding="text"
        />
      }
      customCss={classes.section}>
      <div css={classes.sectionContent}>
        <div css={classes.formRow}>
          <RenderField
            label={dictionary.minimumEducationLevel}
            fieldName="requirements.minimum_education_level"
            render={(controllerArg) => (
              <Select
                options={EDUCATION_LEVELS.map((opt) => ({
                  value: opt.value,
                  label: opt.label[language],
                }))}
                value={
                  EDUCATION_LEVELS.find((opt) => opt.value === controllerArg.value)
                    ? {
                        value: controllerArg.value,
                        label: EDUCATION_LEVELS.find((opt) => opt.value === controllerArg.value)?.label[language] || '',
                      }
                    : undefined
                }
                onChange={(option: { value: string; label: string } | undefined) => {
                  controllerArg.onChange(option?.value || null);
                }}
                closeOnClickOutside
                placeholder={dictionary.minimumEducationLevelPlaceholder}
                customizeValuePill={() => ({ type: 'subtle1' })}
                endIcon={({ visible }) => (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                    }}>
                    {visible ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>
                )}
              />
            )}
          />

          <div css={classes.inputWithDropdown}>
            <RenderField
              label={dictionary.languagesLabel}
              fieldName="requirements.maximum_language_skills"
              render={() => (
                <CustomSelectForm
                  control={control}
                  fieldName="requirements.maximum_language_skills"
                  options={formattedLanguageOptions}
                  label={dictionary.languagesLabel}
                  placeholder={dictionary.languagesPlaceholder}
                  multiple
                  limit
                />
              )}
            />
            <RenderField
              label={dictionary.level}
              fieldName="requirements.languages_level"
              render={(controllerArg) => (
                <Dropdown
                  ref={languagesLevelDropdownRef}
                  placement="bottom-end"
                  zIndex={1400}
                  renderMenu={({ styles }) => (
                    <DropdownMenu
                      customCss={styles}
                      width={150}
                      onSelectOption={(option) => {
                        controllerArg.onChange(option.value);
                        languagesLevelDropdownRef.current?.close();
                      }}
                      options={LANGUAGE_LEVELS.map((opt) => ({
                        value: opt.value,
                        label: opt.label[language],
                        checked: false,
                      }))}
                    />
                  )}>
                  {() => (
                    <div css={classes.dropdownButton}>
                      <ChevronDown size={16} />
                      <Typography variant={(v) => v.supporting.label} color={(c) => c.neutral.variant[70]}>
                        {LANGUAGE_LEVELS.find((opt) => opt.value === controllerArg.value)?.label[language] || ''}
                      </Typography>
                    </div>
                  )}
                </Dropdown>
              )}
            />
          </div>
        </div>

        <div css={classes.formRow}>
          <RenderField
            label={dictionary.certifications}
            fieldName="requirements.certificates"
            render={(controllerArg) => (
              <Input {...controllerArg} type="text" placeholder={dictionary.certificationsPlaceholder} contained />
            )}
          />

          <RenderField
            label={dictionary.driversLicense}
            fieldName="requirements.drivers_license"
            render={() => (
              <CustomSelectForm
                control={control}
                fieldName="requirements.drivers_license"
                options={DRIVER_LICENSE_CATEGORIES}
                label={dictionary.driversLicense}
                multiple
                limit
              />
            )}
          />
        </div>
      </div>
    </CollapsibleDiv>
  );
};
